import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import App from './App'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

// ── hui iframe token injection ──────────────────────────────────────────────
// When CG is embedded in hui via iframe, hui-frontend injects a service account
// access_token via postMessage instead of the user going through Cognito login.
const isEmbedded = window.self !== window.top

let _proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null

function scheduleProactiveRefresh(token: string) {
  if (_proactiveRefreshTimer) clearTimeout(_proactiveRefreshTimer)
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const msUntilRefresh = payload.exp * 1000 - Date.now() - 5 * 60 * 1000
    if (msUntilRefresh > 0) {
      _proactiveRefreshTimer = setTimeout(() => {
        window.parent.postMessage({ type: 'REQUEST_TOKEN' }, '*')
      }, msUntilRefresh)
    }
  } catch {}
}

if (isEmbedded && !localStorage.getItem('access_token')) {
  window.parent.postMessage({ type: 'REQUEST_TOKEN' }, '*')
} else if (isEmbedded) {
  const existing = localStorage.getItem('access_token')
  if (existing) scheduleProactiveRefresh(existing)
}

window.addEventListener('message', (e: MessageEvent) => {
  if (e.data?.type === 'SET_TOKEN' && typeof e.data.token === 'string') {
    if (localStorage.getItem('access_token') === e.data.token) return
    const hadToken = !!localStorage.getItem('access_token')
    localStorage.setItem('access_token', e.data.token)
    scheduleProactiveRefresh(e.data.token)
    if (hadToken) return  // proactive refresh: silently update, no reload needed
    localStorage.removeItem('auth-storage')
    window.location.replace(window.location.pathname + window.location.search)
  }
})
// ────────────────────────────────────────────────────────────────────────────

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </React.StrictMode>,
)
